import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    HelpCircle,
    Menu,
    Mic,
    Sparkles,
    UserIcon,
    Video,
    X,
} from "lucide-react";
import { hasSeenRoomTour, markRoomTourSeen } from "./roomTourStorage";
import TourDemo from "./TourDemo";

type TourTab = "scene" | "video";

export type RoomTourStep = {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    /** Seletor do elemento real a destacar. Sem alvo => card central. */
    target?: string;
    /** Aba que precisa estar ativa (mobile) para o alvo ficar visível. */
    mobileTab?: TourTab;
};

type RoomOnboardingTourProps = {
    isModerator: boolean;
    isMobile: boolean;
    forceOpen?: boolean;
    onForceOpenHandled?: () => void;
    onActivateTab?: (tab: TourTab) => void;
};

type Rect = { top: number; left: number; width: number; height: number };

const SPOT_PADDING = 8;
const TOOLTIP_WIDTH = 320;
const TOOLTIP_GAP = 14;

function findVisibleTarget(selector?: string): HTMLElement | null {
    if (!selector) return null;
    const els = Array.from(document.querySelectorAll<HTMLElement>(selector));
    return (
        els.find((el) => {
            const r = el.getBoundingClientRect();
            return r.width > 0 && r.height > 0 && el.offsetParent !== null;
        }) ||
        null
    );
}

function buildSteps(isModerator: boolean): RoomTourStep[] {
    const steps: RoomTourStep[] = [
        {
            id: "welcome",
            title: "Bem-vindo à sala",
            description:
                "Vamos fazer um tour rápido apresentando cada parte da sala virtual de constelação. Leva menos de 1 minuto.",
            icon: <HelpCircle className="h-6 w-6 text-teal-400" />,
        },
        {
            id: "scene",
            title: "Cenário",
            description:
                "Este é o cenário da constelação. Cada avatar é uma pessoa do campo. Exemplo: toque e arraste um avatar para aproximá-lo ou afastá-lo dos outros, representando as relações.",
            icon: <UserIcon className="h-6 w-6 text-emerald-400" />,
            target: '[data-tour="scene"]',
            mobileTab: "scene",
        },
        {
            id: "video",
            title: "Videochamada",
            description:
                "É aqui que você fala e ouve o grupo, vendo o rosto de cada participante. No celular, toque na aba Videochamada para abrir esta área.",
            icon: <Video className="h-6 w-6 text-blue-400" />,
            target: '[data-tour="video"]',
            mobileTab: "video",
        },
        {
            id: "mic",
            title: "Aviso de microfone",
            description:
                "Quando seu microfone estiver desligado, esta faixa amarela aparece no topo. Toque em Ligar microfone para que todos ouçam você.",
            icon: <Mic className="h-6 w-6 text-amber-400" />,
            target: '[data-tour="mic-banner"]',
        },
        {
            id: "actions",
            title: "Controles da sala",
            description: isModerator
                ? "Por aqui você abre o painel de participantes, torna a sala pública/privada, adiciona avatares e objetos, e encerra a sessão."
                : "Por aqui você acessa o menu da sala, pode rever este tour e sair quando a sessão terminar.",
            icon: <Menu className="h-6 w-6 text-indigo-400" />,
            target: '[data-tour="actions"]',
        },
    ];

    if (isModerator) {
        steps.push({
            id: "moderator",
            title: "Ferramentas do terapeuta",
            description:
                "Como moderador, use os controles para adicionar avatares virtuais e objetos ao cenário e conduzir a sessão.",
            icon: <Sparkles className="h-6 w-6 text-violet-400" />,
            target: '[data-tour="actions"]',
        });
    }

    steps.push({
        id: "finish",
        title: "Tudo pronto!",
        description:
            "Você concluiu o tour. Para revê-lo a qualquer momento, abra o menu da sala e toque em Ver tour da sala.",
        icon: <Box className="h-6 w-6 text-teal-400" />,
    });

    return steps;
}

const RoomOnboardingTour: React.FC<RoomOnboardingTourProps> = ({
    isModerator,
    isMobile,
    forceOpen = false,
    onForceOpenHandled,
    onActivateTab,
}) => {
    const steps = useMemo(() => buildSteps(isModerator), [isModerator]);

    const [isOpen, setIsOpen] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [rect, setRect] = useState<Rect | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
    const [arrow, setArrow] = useState<{
        ax: number;
        ay: number;
        cx: number;
        cy: number;
        mx: number;
        my: number;
    } | null>(null);

    const step = steps[stepIndex];

    useEffect(() => {
        if (forceOpen) {
            setStepIndex(0);
            setIsOpen(true);
            onForceOpenHandled?.();
            return;
        }
        // Abre automaticamente apenas na 1ª vez (DEV e produção). Depois de visto,
        // só reabre pelo menu "Ver tour da sala" (forceOpen). Isso evita o tour
        // reaparecendo várias vezes em DEV.
        if (!hasSeenRoomTour()) {
            setIsOpen(true);
        }
    }, [forceOpen, onForceOpenHandled]);

    const measureTarget = useCallback(() => {
        if (!step?.target) {
            setRect(null);
            return;
        }
        const el = findVisibleTarget(step.target);
        if (!el) {
            setRect(null);
            return;
        }
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, [step]);

    // Ao trocar de passo: ativa a aba certa (mobile) e mede o alvo após o layout assentar.
    useEffect(() => {
        if (!isOpen || !step) return;

        if (isMobile && step.mobileTab && onActivateTab) {
            onActivateTab(step.mobileTab);
        }

        let raf = 0;
        const timer = window.setTimeout(() => {
            raf = window.requestAnimationFrame(measureTarget);
        }, 220);

        return () => {
            window.clearTimeout(timer);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, [isOpen, step, isMobile, onActivateTab, measureTarget]);

    // Recalcula em resize/scroll/rotação.
    useEffect(() => {
        if (!isOpen) return;
        const handler = () => measureTarget();
        window.addEventListener("resize", handler);
        window.addEventListener("orientationchange", handler);
        window.addEventListener("scroll", handler, true);
        return () => {
            window.removeEventListener("resize", handler);
            window.removeEventListener("orientationchange", handler);
            window.removeEventListener("scroll", handler, true);
        };
    }, [isOpen, measureTarget]);

    // Posiciona a tooltip junto ao alvo (ou centraliza quando não há alvo).
    useLayoutEffect(() => {
        if (!isOpen) return;

        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const width = Math.min(TOOLTIP_WIDTH, vw - 24);
        const height = tooltipRef.current?.offsetHeight ?? 220;

        if (!rect) {
            setTooltipStyle({
                width,
                left: Math.round((vw - width) / 2),
                top: Math.round((vh - height) / 2),
            });
            setArrow(null);
            return;
        }

        const spaceBelow = vh - (rect.top + rect.height);
        const placeBelow = spaceBelow > height + TOOLTIP_GAP + 16;

        let top = placeBelow
            ? rect.top + rect.height + TOOLTIP_GAP
            : rect.top - height - TOOLTIP_GAP;
        top = Math.max(12, Math.min(top, vh - height - 12));

        let left = rect.left + rect.width / 2 - width / 2;
        left = Math.max(12, Math.min(left, vw - width - 12));

        setTooltipStyle({ width, left: Math.round(left), top: Math.round(top) });

        // Seta curva conectando o balão ao elemento destacado.
        const targetCx = rect.left + rect.width / 2;
        const tooltipCx = left + width / 2;
        if (placeBelow) {
            const ay = top; // topo do balão
            const my = rect.top + rect.height; // base do alvo
            setArrow({
                ax: tooltipCx,
                ay,
                mx: targetCx,
                my,
                cx: (tooltipCx + targetCx) / 2 + 26,
                cy: (ay + my) / 2,
            });
        } else {
            const ay = top + height; // base do balão
            const my = rect.top; // topo do alvo
            setArrow({
                ax: tooltipCx,
                ay,
                mx: targetCx,
                my,
                cx: (tooltipCx + targetCx) / 2 + 26,
                cy: (ay + my) / 2,
            });
        }
    }, [rect, isOpen, stepIndex]);

    const finish = useCallback((remember: boolean) => {
        if (remember) markRoomTourSeen();
        setIsOpen(false);
        setStepIndex(0);
        setRect(null);
    }, []);

    if (!isOpen || !step) {
        return null;
    }

    const isLast = stepIndex === steps.length - 1;
    const isFirst = stepIndex === 0;

    return (
        <div className="fixed inset-0 z-[80]">
            {/* Overlay escuro com "buraco" no alvo (spotlight via box-shadow). */}
            {rect ? (
                <>
                    {/* Recorte iluminado */}
                    <div
                        className="pointer-events-none fixed rounded-xl ring-2 ring-amber-400 transition-all duration-300"
                        style={{
                            top: rect.top - SPOT_PADDING,
                            left: rect.left - SPOT_PADDING,
                            width: rect.width + SPOT_PADDING * 2,
                            height: rect.height + SPOT_PADDING * 2,
                            boxShadow: "0 0 0 9999px rgba(2, 6, 23, 0.80)",
                        }}
                    />
                    {/* Eco "radar" pulsante ao redor do alvo */}
                    <div
                        className="pointer-events-none fixed rounded-xl border-2 border-amber-300/70 animate-ping"
                        style={{
                            top: rect.top - SPOT_PADDING,
                            left: rect.left - SPOT_PADDING,
                            width: rect.width + SPOT_PADDING * 2,
                            height: rect.height + SPOT_PADDING * 2,
                            animationDuration: "1.8s",
                        }}
                    />
                </>
            ) : (
                <div className="pointer-events-none fixed inset-0 bg-slate-950/80" />
            )}

            {/* Camada clicável para "pular" tocando fora (sem fechar acidentalmente o alvo). */}
            <button
                type="button"
                aria-label="Fechar tour"
                onClick={() => finish(true)}
                className="absolute inset-0 h-full w-full cursor-default"
            />

            {/* Demonstração animada sobre o elemento real (apenas visual) */}
            {rect && <TourDemo stepId={step.id} rect={rect} />}

            {/* Seta curva + marcador circular conectando o balão ao elemento */}
            {arrow && (
                <>
                    <svg
                        className="pointer-events-none fixed inset-0 h-screen w-screen"
                        width="100%"
                        height="100%"
                    >
                        <defs>
                            <marker
                                id="room-tour-arrowhead"
                                markerWidth="10"
                                markerHeight="10"
                                refX="6"
                                refY="3"
                                orient="auto"
                            >
                                <path d="M0,0 L6,3 L0,6 Z" fill="#fbbf24" />
                            </marker>
                        </defs>
                        <path
                            d={`M ${arrow.ax} ${arrow.ay} Q ${arrow.cx} ${arrow.cy} ${arrow.mx} ${arrow.my}`}
                            fill="none"
                            stroke="#fbbf24"
                            strokeWidth="2.5"
                            strokeDasharray="6 6"
                            markerEnd="url(#room-tour-arrowhead)"
                        />
                    </svg>
                    {/* Marcador pulsante no ponto de interesse */}
                    <div
                        className="pointer-events-none fixed -translate-x-1/2 -translate-y-1/2"
                        style={{ left: arrow.mx, top: arrow.my }}
                    >
                        <span className="relative flex h-4 w-4">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400/70" />
                            <span className="relative inline-flex h-4 w-4 rounded-full border-2 border-white bg-amber-500" />
                        </span>
                    </div>
                </>
            )}

            {/* Tooltip / card do passo */}
            <div
                ref={tooltipRef}
                className="fixed z-[81] rounded-2xl border border-slate-600/80 bg-slate-800 p-5 shadow-2xl"
                style={tooltipStyle}
                role="dialog"
                aria-modal="true"
                aria-labelledby="room-tour-title"
            >
                <button
                    type="button"
                    onClick={() => finish(true)}
                    className="absolute right-3 top-3 rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-700 hover:text-white"
                    aria-label="Fechar tour"
                >
                    <X className="h-4 w-4" />
                </button>

                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-slate-700/80">
                    {step.icon}
                </div>

                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    Passo {stepIndex + 1} de {steps.length}
                </p>
                <h2 id="room-tour-title" className="mb-1.5 text-base font-bold text-white">
                    {step.title}
                </h2>
                <p className="text-sm leading-relaxed text-slate-300">{step.description}</p>

                <div className="mt-4 flex items-center justify-center gap-1.5">
                    {steps.map((s, i) => (
                        <span
                            key={s.id}
                            className={`h-1.5 rounded-full transition-all ${
                                i === stepIndex ? "w-5 bg-amber-400" : "w-1.5 bg-slate-600"
                            }`}
                            aria-hidden
                        />
                    ))}
                </div>

                <div className="mt-4 flex gap-2">
                    {!isFirst && (
                        <button
                            type="button"
                            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                            className="flex-1 rounded-full border border-slate-600 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-700/50"
                        >
                            Voltar
                        </button>
                    )}
                    {!isLast ? (
                        <button
                            type="button"
                            onClick={() => setStepIndex((i) => Math.min(steps.length - 1, i + 1))}
                            className="flex-1 rounded-full bg-amber-500 py-2 text-sm font-semibold text-amber-950 transition-colors hover:bg-amber-400"
                        >
                            Próximo
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => finish(true)}
                            className="flex-1 rounded-full bg-amber-500 py-2 text-sm font-semibold text-amber-950 transition-colors hover:bg-amber-400"
                        >
                            Concluir
                        </button>
                    )}
                </div>

                {!isLast && (
                    <button
                        type="button"
                        onClick={() => finish(true)}
                        className="mt-2 w-full text-center text-xs text-slate-400 hover:text-slate-200"
                    >
                        Pular tour
                    </button>
                )}
            </div>
        </div>
    );
};

export default RoomOnboardingTour;
